import type { Ref } from 'vue';
import { watch } from 'vue';
import { useField } from 'vee-validate';

import { buildBrandingFormValues } from '@/helpers/community-branding.helper';
import type { BrandingPresetConfigurationItem, CommunityBrandingMode, CommunityDto } from '@/services/types/api.types';

type FormSetFieldValue = (field: string, value: unknown) => void;

interface UseCommunityBrandingFormOptions {
  presets: Ref<BrandingPresetConfigurationItem[]>;
  setFieldValue: FormSetFieldValue;
  values: Partial<CommunityDto>;
}

export function useCommunityBrandingForm({ presets, setFieldValue, values }: UseCommunityBrandingFormOptions) {
  const { value: brandingModeField } = useField<CommunityBrandingMode | null>('options.branding.mode');
  const { value: brandingPresetKeyField } = useField<string | null>('options.branding.preset_key');
  const { value: headingFontSourceField } = useField<'builtin' | 'remote-css' | null>(
    'options.branding.fonts.heading.source',
  );
  const { value: bodyFontSourceField } = useField<'builtin' | 'remote-css' | null>(
    'options.branding.fonts.body.source',
  );

  function applyBrandingPresetDefaults(
    presetKey: string | null | undefined,
    options?: { preserveCurrentValues?: boolean },
  ) {
    const preserveCurrentValues = options?.preserveCurrentValues ?? false;
    const currentBranding = preserveCurrentValues ? values.options?.branding : null;
    const nextBranding = buildBrandingFormValues(
      preserveCurrentValues
        ? {
            ...currentBranding,
            mode: 'custom',
            preset_key: presetKey ?? undefined,
          }
        : {
            mode: 'default',
            preset_key: presetKey ?? undefined,
          },
      presets.value,
    );

    setFieldValue('options.branding.preset_key', nextBranding.preset_key);
    setFieldValue('options.branding.tokens.colors', nextBranding.tokens.colors);
    setFieldValue('options.branding.tokens.typography', nextBranding.tokens.typography);
    setFieldValue('options.branding.fonts.heading', nextBranding.fonts.heading);
    setFieldValue('options.branding.fonts.body', nextBranding.fonts.body);
  }

  watch([brandingModeField, brandingPresetKeyField], ([mode, presetKey], [previousMode, previousPresetKey]) => {
    const modeChanged = mode !== previousMode;
    const presetChanged = presetKey !== previousPresetKey;

    if (mode === 'custom') {
      if (modeChanged && previousMode && previousMode !== 'custom') {
        applyBrandingPresetDefaults(presetKey, { preserveCurrentValues: true });
      }
      return;
    }

    if (modeChanged || presetChanged) {
      applyBrandingPresetDefaults(presetKey);
    }
  });

  watch(headingFontSourceField, (newVal) => {
    if (newVal === 'builtin') {
      setFieldValue('options.branding.fonts.heading.css_url', null);
      return;
    }

    if (newVal === 'remote-css') {
      setFieldValue('options.branding.fonts.heading.builtin_key', null);
      return;
    }

    setFieldValue('options.branding.fonts.heading.builtin_key', null);
    setFieldValue('options.branding.fonts.heading.css_url', null);
  });

  watch(bodyFontSourceField, (newVal) => {
    if (newVal === 'builtin') {
      setFieldValue('options.branding.fonts.body.css_url', null);
      return;
    }

    if (newVal === 'remote-css') {
      setFieldValue('options.branding.fonts.body.builtin_key', null);
      return;
    }

    setFieldValue('options.branding.fonts.body.builtin_key', null);
    setFieldValue('options.branding.fonts.body.css_url', null);
  });
}
